import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { Section, Eyebrow, FadeIn } from "@/components/Section";
import useSEO from "@/hooks/useSEO";

const WARSAW_IMG =
  "https://images.unsplash.com/photo-1519197924294-4ba991a11128?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHx3YXJzYXclMjBjaXR5c2NhcGUlMjBuaWdodHxlbnwwfHx8fDE3ODA5MjU0NjN8MA&ixlib=rb-4.1.0&q=85";

const TEAM_IMG =
  "https://images.unsplash.com/photo-1568992688065-536aad8a12f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3ODA5MjU0NjR8MA&ixlib=rb-4.1.0&q=85";

const values = [
  {
    n: "01",
    t: "Revenue, not vanity",
    d: "We optimise for blended ROAS and contribution margin — not CTR screenshots.",
  },
  {
    n: "02",
    t: "Senior operators only",
    d: "Your account is run by a strategist with 6+ years on accounts above €100k/mo.",
  },
  {
    n: "03",
    t: "Radical transparency",
    d: "You own every account. We share every report. No hidden fees, no lock-ins.",
  },
  {
    n: "04",
    t: "Speed of execution",
    d: "Most ideas are live within 72 hours. Iteration loops measured in days.",
  },
];

export default function About() {
  useSEO({
    title: "About · Warsaw-based growth agency",
    description:
      "Bright Solutions Europe is a senior-only performance marketing studio headquartered in Warsaw, operating across Poland, DACH, the Nordics and the UK.",
    path: "/about",
  });
  return (
    <div data-testid="page-about">
      <Section className="pt-36">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <FadeIn>
              <Eyebrow>About us</Eyebrow>
            </FadeIn>
            <FadeIn delay={0.05}>
              <h1 className="font-display mt-5 text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.9]">
                A Warsaw studio
                <br />
                with a <span className="text-[#ccff00]">European</span>
                <br />
                growth mandate.
              </h1>
            </FadeIn>
          </div>
          <FadeIn delay={0.1} className="md:col-span-5">
            <p className="text-zinc-400 text-lg leading-relaxed">
              We built Bright Solutions Europe because the agency model was
              broken. Too many decks, too few decisions. Too much spend, too
              little revenue.
            </p>
            <p className="mt-4 text-zinc-400 text-lg leading-relaxed">
              Today, we run growth for product, ecommerce and SaaS brands
              scaling beyond their first €1M — from our headquarters in central
              Warsaw.
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Warsaw block */}
      <Section
        dataTestid="about-warsaw"
        className="border-t border-white/10 bg-[#0a0a0b]"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <FadeIn className="md:col-span-7">
            <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
              <img
                src={WARSAW_IMG}
                alt="Warsaw cityscape at night"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#ccff00]">
                <MapPin className="h-4 w-4" /> 52.225 °N · 21.024 °E
              </div>
            </div>
          </FadeIn>

          <div className="md:col-span-5 flex flex-col justify-center">
            <FadeIn delay={0.05}>
              <Eyebrow>Headquarters</Eyebrow>
              <h2 className="font-display mt-4 text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
                Warsaw.
                <br />
                <span className="text-[#ccff00]">Operating Europe-wide.</span>
              </h2>
              <p className="mt-5 text-zinc-400 leading-relaxed">
                We work with brands across Poland, DACH, the Nordics and the UK
                — in English, Polish and German. Localised funnels, native
                creative, fluent strategy.
              </p>
              <div className="mt-8 font-mono text-sm text-zinc-300 space-y-1">
                <p className="text-white font-medium">
                  BRIGHT SOLUTIONS EUROPE sp. z o.o.
                </p>
                <p>ul. Hoża 86 / 410</p>
                <p>00-682 Warszawa, Poland</p>
                <p className="text-zinc-500 text-xs mt-2">
                  REGON 7011162230
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Values */}
      <Section dataTestid="about-values" className="border-t border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Eyebrow>Operating principles</Eyebrow>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
              What we
              <br />
              <span className="text-[#ccff00]">refuse</span> to
              <br />
              compromise on.
            </h2>
          </div>

          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10">
            {values.map((v) => (
              <div
                key={v.n}
                className="bg-[#0f0f10] p-8 flex flex-col gap-3 hover:bg-[#141416] transition"
              >
                <span className="font-mono text-xs text-zinc-500">{v.n}</span>
                <h3 className="font-display text-2xl font-bold tracking-tight">
                  {v.t}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Team image block */}
      <Section className="border-t border-white/10 bg-[#0a0a0b]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <FadeIn className="md:col-span-6">
            <Eyebrow>The team</Eyebrow>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
              Small team.
              <br />
              <span className="text-[#ccff00]">Senior craft.</span>
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              Strategists, performance leads, creative producers and SEO
              technicians — all senior, all in-house. No subcontracting, no
              hand-offs to interns.
            </p>
            <Link
              to="/contact"
              data-testid="about-team-cta"
              className="mt-8 inline-flex items-center gap-2 bg-[#ccff00] hover:bg-[#d4ff33] text-black px-5 py-3 font-bold tracking-tight transition group"
            >
              Talk to a strategist
              <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeIn>
          <FadeIn delay={0.1} className="md:col-span-6">
            <div className="aspect-[5/4] overflow-hidden border border-white/10">
              <img
                src={TEAM_IMG}
                alt="Modern marketing team meeting"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </FadeIn>
        </div>
      </Section>
    </div>
  );
}
